import {
  NgZone,
  Component,
  DestroyRef,
  OnInit,
  AfterViewInit,
  inject,
  ViewChild,
  ElementRef,
  HostListener,
  Renderer2,
  Type,
  ChangeDetectionStrategy,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { ActiveSessionCountComponent } from '../../../components/active-session-count/active-session-count.component'
import { PersonaBriefingComponent } from '../../../components/persona-briefing/persona-briefing.component'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import {
  WebSocketService,
  TranscriptionResult,
  BridgeAudioEvent,
  InterimTranscriptEvent,
  UserTurnCompleteEvent,
  SttErrorEvent,
  AIResponse,
} from '../../../services/websocket.service'
import { AudioStreamingService } from '../../../services/audio-streaming.service'
import {
  AudioRecordingService,
  AudioLevelInfo,
} from '../../../services/audio-recording.service'
import {
  SpeechRecognitionService,
  TranscriptResult as SpeechRecognitionResult,
} from '../../../services/speech-recognition.service'
import { AudioAnalyzerService } from '../../../services/audio-analyzer.service'
import { AudioFormatAdapterService } from '../../../services/roleplay/audio-format-adapter.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  Observable,
  Subject,
  animationFrameScheduler,
  auditTime,
  firstValueFrom,
  interval,
  observeOn,
  pairwise,
  filter,
  takeUntil,
} from 'rxjs'
import { RoleplayStore } from '../../../stores/roleplay.store'
import { UserStore } from '../../../stores/user.store'
import { isFeatureAllowed } from '../../../config/feature-permission.config'
import { PersonaData } from '../../../services/persona/persona.mapping'
import { AudioDevicesService } from '../../../services/audio-devices.service'
import { FormsModule } from '@angular/forms'
import testvoice from '../../../../assets/roleplay-test.json'
import { environment } from '../../../../environments/environment'
import { AudioQueueService } from '../../../services/roleplay/audio-queue.service'
import {
  DialogueStateMachineService,
  DialogState as DlgState,
} from '../../../services/roleplay/dialogue-state-machine.service'
import { RoleplayWebSocketFacadeService } from '../../../services/roleplay/roleplay-websocket-facade.service'
import { RoleplaySessionService } from '../../../services/roleplay/roleplay-session.service'
import {
  RoleplayAssessmentData,
  AssessmentLevel,
  IRoleplaySoundEffect,
  IRoleplayAssessmentDisplay,
} from '../../../services/roleplay/roleplay-assessment.types'
import {
  ROLEPLAY_SOUND_EFFECT,
  ROLEPLAY_HISTORY_ASSESSMENT_DISPLAY,
} from '../../../services/roleplay/addon/roleplay-addon.tokens'
import { RoleplaySimpleSessionAssessmentComponent } from '../../../addon/roleplay/simple/session/roleplay-simple-session-assessment.component'
import {
  AudioCaptureStrategyFactory,
  RecognitionMode,
} from '../../../services/roleplay/audio-capture-strategy-factory.service'
import { IAudioCaptureStrategy } from '../../../services/roleplay/audio-capture-strategy.interface'
import { CloudSttTurnCoordinatorService } from '../../../services/roleplay/cloud-stt-turn-coordinator.service'
import { DebugVisualizationService } from '../../../services/debug-visualization.service'
import { SIMPLE_SOUND_EFFECT_CONFIG } from '../../../services/roleplay/addon/roleplay-sound-effect.service'

type ViewMode = 'PREPARE' | 'DIALOG' | 'HISTORY'
type DialogState =
  | 'USER_SPEAKING'
  | 'PROCESSING'
  | 'AI_SPEAKING'
  | 'LISTENING'
  | 'IDLE'

interface ConversationMessage {
  type: 'user' | 'ai' | 'assessment'
  content: string
  timestamp: Date
  audioData?: Blob | string
  confidence?: number
  // 評価ログ用フィールド（type: 'assessment'時のみ使用）
  assessment?: AssessmentLevel
  reason?: string
  speaker?: string
  reasonDetail?: string
  score?: number
  // デバッグ注入識別用メタデータ（WebSocket経由・デバッグ経由問わず透過的に記録）
  metadata?: Record<string, unknown>
}

@Component({
  selector: 'app-roleplay-session-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ActiveSessionCountComponent,
    PersonaBriefingComponent,
  ],
  templateUrl: './roleplay-session-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './roleplay-session-page.component.scss',
})
export class RoleplaySessionPageComponent implements OnInit, AfterViewInit {
  static debugCounter = 0
  private destroyRef = inject(DestroyRef)
  private renderer = inject(Renderer2)
  private startBtnUnlisteners: (() => void)[] = []
  private sessionEnded$ = new Subject<void>()
  private isWaitingForAiResponse = false
  userId: string | null = null
  assessmentId: string | null = null
  isTransitioning = false

  sessionId!: string
  isConnecting = false
  private isStartingSession = false
  isSessionActive = false
  isAIProcessing = false
  isAIPlaying = false
  sessionDurationSeconds = 0 // 会話時間（秒）- リアルタイム更新用
  conversations: ConversationMessage[] = []
  currentAssessment: RoleplayAssessmentData | undefined
  transcriptions: TranscriptionResult[] = []
  currentAudioInfo: AudioLevelInfo = {
    currentLevel: 0,
    isDetectingVoice: false,
    speechDuration: 0,
    confidence: 0,
  }

  // 音声認識モード切替
  // 'webSpeech' = Web Speech API, 'mediaRecorder' = Server STT (batch), 'cloudSTT' = Cloud STT Streaming
  recognitionMode: 'webSpeech' | 'mediaRecorder' | 'cloudSTT' = 'cloudSTT'
  useSpeechRecognition = true // 後方互換性のため維持 (recognitionMode === 'webSpeech')
  speechRecognitionAvailable = false

  // Cloud STT Streaming state
  private isCloudSttTurnActive = false
  private lastTurnStartTime: number | null = null // ターン開始時刻（重複防止用）
  private aiTurnCompleteReceived = false // AI turn complete受信フラグ（音声再生完了後にユーザーターン開始用）
  currentInterimText = '' // リアルタイム認識テキスト表示用
  captionsFeatureEnabled = false // フィーチャーフラグ (ROLEPLAY_CAPTIONS)
  captionsEnabled = false // 字幕ON/OFF
  lowLatencyFeatureEnabled = false // フィーチャーフラグ (ROLEPLAY_LOW_LATENCY)。低遅延の新経路 on/off。解決失敗時は false = 旧経路。後続 PR2 で経路分岐に使用予定のため未参照でも削除しない
  // 2者通話: 現在の話者
  currentSpeaker: string | null = null

  // カスタムアラートダイアログ設定
  alertConfig: {
    type: 'alert' | 'confirm'
    message: string
    confirmText: string
    cancelText: string
    resolve?: (value: boolean) => void
  } | null = null

  private skipDeactivateConfirm = false

  // PreparePage Meta
  sessionTitle = ''
  selectedPersonaId = ''
  personaData: PersonaData | null = null
  selectedScenario: { id: string; name: string } | null = null
  personas: PersonaData[] = []
  private personaMap = new Map<string, PersonaData>()

  micTestRunning = false
  micTitle = ''
  mics$!: Observable<MediaDeviceInfo[]>
  speakers$!: Observable<MediaDeviceInfo[]>
  selectedMicId$!: Observable<string | null>
  selectedSpeakerId$!: Observable<string | null>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sinkCapable = 'setSinkId' in (HTMLMediaElement.prototype as any)

  rms = 0
  isSwitchingMic = false

  micPermissionDenied$!: Observable<boolean>
  permissionErrorMsg$!: Observable<string | null>

  // アドオントークン（optional inject: 未提供時はnull → 評価非表示・SE非再生）
  private soundEffect: IRoleplaySoundEffect | null = inject(
    ROLEPLAY_SOUND_EFFECT,
    { optional: true },
  )
  private historyAssessmentDisplayType = inject(
    ROLEPLAY_HISTORY_ASSESSMENT_DISPLAY,
    { optional: true },
  )

  // iPad Safari では Angular のテンプレートイベントバインディングが発火しないため、
  // Renderer2 で直接イベントリスナーを登録する。
  // ボタンは @if で再生成されるため、setter で毎回再登録する。
  @ViewChild('startBtn', { static: false })
  set startBtnRef(ref: ElementRef<HTMLButtonElement> | undefined) {
    this.startBtnUnlisteners.forEach((fn) => fn())
    this.startBtnUnlisteners = []
    if (!ref) return
    const btn = ref.nativeElement
    this.startBtnUnlisteners.push(
      this.renderer.listen(btn, 'touchend', (e: Event) => {
        if (btn.disabled) return
        e.preventDefault()
        this.startSession()
      }),
      this.renderer.listen(btn, 'click', () => {
        if (btn.disabled) return
        this.startSession()
      }),
    )
  }

  @ViewChild('aiAudio', { static: true })
  aiAudioRef?: ElementRef<HTMLAudioElement>

  @ViewChild('soundEffectAudio', { static: false })
  soundEffectAudioRef?: ElementRef<HTMLAudioElement>

  @ViewChild('waveformCanvas', { static: false })
  set waveformCanvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (ref) {
      this.audioAnalyzerService.setCanvas(ref.nativeElement)
      this.debugVisualizationService.setCanvas(ref.nativeElement)
    }
  }

  vadSettings = this.audioRecordingService.getVADSettings()

  // 波形デバッグ表示用（隠しモード）
  showWaveformDebug = false
  private avatarTapCount = 0
  private avatarTapTimer: ReturnType<typeof setTimeout> | null = null

  // デバッグJSON注入用
  debugJsonInput = JSON.stringify(
    {
      sessionId: '',
      response: 'テスト応答テキスト',
      speaker: '母',
      assessment: 'Good',
      reason: '適切な内容を含んでいるため',
      reason_detail: '詳細な評価理由テキスト',
      score: 60,
      isFinished: false,
      audioData: '',
      timestamp: '',
      processingTime: 0,
    },
    null,
    2,
  )
  debugJsonError: string | null = null

  view: ViewMode = 'PREPARE'
  state: DialogState = 'IDLE'

  private startSilenceUntil = 0

  async canDeactivate(): Promise<boolean> {
    if (this.skipDeactivateConfirm) {
      return true
    }
    if (this.view === 'HISTORY') {
      // return await this.showConfirm(
      //   'このページを離れると現在の会話履歴は再表示できません。履歴画面を離れますか？',
      // )
    }
    return true
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.view === 'HISTORY') {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  // ===== Phase 2: Strategy Pattern =====
  // eslint-disable-next-line @typescript-eslint/member-ordering
  private currentStrategy: IAudioCaptureStrategy | null = null
  // eslint-disable-next-line @typescript-eslint/member-ordering
  private strategyFactory = inject(AudioCaptureStrategyFactory)

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private webSocketService: WebSocketService,
    private audioRecordingService: AudioRecordingService,
    private speechRecognitionService: SpeechRecognitionService,
    private audioStreamingService: AudioStreamingService,
    public audioAnalyzerService: AudioAnalyzerService,
    private zone: NgZone,
    private roleplayStore: RoleplayStore,
    private userStore: UserStore,
    private audioDevices: AudioDevicesService,
    private audioQueueService: AudioQueueService,
    private dialogueStateMachine: DialogueStateMachineService,
    private webSocketFacade: RoleplayWebSocketFacadeService,
    private sessionService: RoleplaySessionService,
    private turnCoordinator: CloudSttTurnCoordinatorService,
    private debugVisualizationService: DebugVisualizationService,
    private audioFormatAdapterService: AudioFormatAdapterService,
  ) {
    this.sessionId = this.sessionService.generateSessionId()
    console.log(`Initial Session ID generated: ${this.sessionId}`)

    this.destroyRef.onDestroy(() => {
      this.startBtnUnlisteners.forEach((fn) => fn())
      this.startBtnUnlisteners = []
      this.state = 'IDLE'
      this.audioAnalyzerService.cleanup()
      this.debugVisualizationService.cleanup()
      // Cloud STT cleanup
      if (this.recognitionMode === 'cloudSTT') {
        this.audioStreamingService.cleanup()
      }
      this.endSession()
      this.webSocketService.disconnect()
      if (this.avatarTapTimer) {
        clearTimeout(this.avatarTapTimer)
      }
      // this.wave.stop();
    })
  }

  /** DIALOGビュー用: 評価表示コンポーネント型 */
  get sessionAssessmentComponent(): Type<IRoleplayAssessmentDisplay> | null {
    this.soundEffect?.configure(SIMPLE_SOUND_EFFECT_CONFIG)
    return RoleplaySimpleSessionAssessmentComponent
  }

  /** HISTORYビュー用: アドオンコンポーネント型（テンプレートバインディング用） */
  get historyAssessmentComponent(): Type<IRoleplayAssessmentDisplay> | null {
    return this.historyAssessmentDisplayType ?? null
  }

  /**
   * HISTORYビュー用: AIメッセージに紐付く評価データを取得する。
   * conversations[index - 1]がtype: 'assessment'の場合、RoleplayAssessmentDataを返す。
   *
   * @param index - conversations配列内のAIメッセージのインデックス
   * @returns 紐付く評価データ、またはundefined
   */
  getAssessmentForAiMessage(index: number): RoleplayAssessmentData | undefined {
    if (index <= 0) {
      return undefined
    }
    const prev = this.conversations[index - 1]
    if (prev == null || prev.type !== 'assessment') {
      return undefined
    }
    return {
      speaker: prev.speaker,
      assessment: prev.assessment,
      reason: prev.reason,
      reasonDetail: prev.reasonDetail,
      score: prev.score,
      isFinished: undefined,
    }
  }

  /**
   * HISTORYビュー用: AIメッセージの話者名を解決する。
   * 直前のassessmentエントリにspeakerがあればそれを返し、なければselectedPersona.nameを返す。
   *
   * @param index - conversations配列内のAIメッセージのインデックス
   * @returns 話者名
   */
  getSpeakerForAiMessage(index: number): string {
    if (index > 0) {
      const prev = this.conversations[index - 1]
      if (
        prev != null &&
        prev.type === 'assessment' &&
        prev.speaker != null &&
        prev.speaker !== ''
      ) {
        return prev.speaker
      }
    }
    return this.selectedPersona?.name ?? '無名'
  }

  ngAfterViewInit(): void {
    if (this.soundEffect != null && this.soundEffectAudioRef != null) {
      this.soundEffect.init(this.soundEffectAudioRef.nativeElement)
    }
  }

  private toListening() {
    this.state = 'LISTENING'
    this.audioAnalyzerService.setDialogState(this.state)
    this.debugVisualizationService.setDialogState(this.state)

    // ===== Phase 1: Use DialogueStateMachine =====
    this.dialogueStateMachine.transition(DlgState.LISTENING)
  }

  private toUserTalking() {
    this.state = 'USER_SPEAKING'
    this.audioAnalyzerService.setDialogState(this.state)
    this.debugVisualizationService.setDialogState(this.state)

    // ===== Phase 1: Use DialogueStateMachine =====
    this.dialogueStateMachine.transition(DlgState.USER_SPEAKING)
  }

  private toProcessing() {
    this.state = 'PROCESSING'
    this.audioAnalyzerService.setDialogState(this.state)
    this.debugVisualizationService.setDialogState(this.state)

    // ===== Phase 1: Use DialogueStateMachine =====
    this.dialogueStateMachine.transition(DlgState.PROCESSING)
  }

  private toAiSpeaking() {
    this.state = 'AI_SPEAKING'
    this.audioAnalyzerService.setDialogState(this.state)
    this.debugVisualizationService.setDialogState(this.state)

    // ===== Phase 1: Use DialogueStateMachine =====
    this.dialogueStateMachine.transition(DlgState.AI_SPEAKING)
  }

  async onPersonaChange(personaId: string) {
    this.selectedPersonaId = personaId
    if (!personaId) {
      this.personaData = null
      return
    }

    await this.loadPersonaData(personaId)
  }

  private async loadPersonas(): Promise<void> {
    try {
      this.personas = await firstValueFrom(this.roleplayStore.getPersonas())

      this.personaMap = new Map(this.personas.map((p) => [p.id, p]))

      if (this.personas.length > 0) {
        this.selectedPersonaId = this.personas[0].id
        await this.loadPersonaData(this.personas[0].id)
      }
    } catch (error) {
      console.error('Failed to load personas:', error)
      this.personas = []
      void this.showAlert('ペルソナ一覧の取得に失敗しました。')
    }
  }

  get selectedPersona(): PersonaData | null {
    return this.personaMap.get(this.selectedPersonaId) ?? null
  }

  async ngOnInit() {
    await this.loadPersonas()
    this.mics$ = this.audioDevices.mics$
    this.speakers$ = this.audioDevices.speakers$
    this.selectedMicId$ = this.audioDevices.selectedMicId$
    this.selectedSpeakerId$ = this.audioDevices.selectedSpeakerId$

    await this.audioDevices.ensureLabels()
    this.micPermissionDenied$ = this.audioDevices.micPermissionDenied$
    this.permissionErrorMsg$ = this.audioDevices.permissionErrorMsg$

    // オーディオ設定を初期化
    this.audioAnalyzerService.audioSettings =
      this.audioRecordingService.getAudioSettings()

    // Firebase Auth から userId を取得
    if (typeof window !== 'undefined') {
      try {
        const { getAuth } = await import('firebase/auth')
        const auth = getAuth()
        const user = auth.currentUser
        if (user) {
          this.userId = user.uid
          console.log('User ID obtained:', this.userId)
        }
      } catch (error) {
        console.error('Failed to get user ID:', error)
      }
    }

    await this.resolveFeatureFlags()

    const personaId = this.route.snapshot.queryParams['personaId']
    if (personaId) {
      this.selectedPersonaId = personaId
      await this.loadPersonaData(personaId)
    }

    // Speech Recognition利用可能性チェック
    this.speechRecognitionAvailable =
      this.speechRecognitionService.isAvailable()

    this.setupSubscriptions()
  }

  private async resolveFeatureFlags(): Promise<void> {
    try {
      const ctxUser = await firstValueFrom(
        this.userStore.getOneSelf({ useCache: true }),
      )
      this.captionsFeatureEnabled = isFeatureAllowed(
        'ROLEPLAY_CAPTIONS',
        ctxUser.enabledFeatures,
      )
      this.lowLatencyFeatureEnabled = isFeatureAllowed(
        'ROLEPLAY_LOW_LATENCY',
        ctxUser.enabledFeatures,
      )
    } catch (error) {
      console.error('Failed to resolve feature flags:', error)
      this.captionsFeatureEnabled = false
      this.lowLatencyFeatureEnabled = false
    }
  }

  private async loadPersonaData(personaId: string): Promise<void> {
    try {
      this.personaData = await this.roleplayStore.fetchPersonaById(personaId)
      if (!this.personaData) {
        throw new Error(`Persona not found: ${personaId}`)
      }

      console.log('Persona data loaded:', this.personaData)
    } catch (error) {
      console.error('Failed to load persona data:', error)
      this.personaData = null
      void this.showAlert('ペルソナデータの読み込みに失敗しました。')
    }
  }

  private setupSubscriptions(): void {
    // ===== WebSocket再接続時のCloud STTターン再開 =====
    // NOTE: takeUntil(sessionEnded$) は使用しない。sessionEnded$ はセッション終了時に
    // 新しい Subject に差し替えられるため、takeUntilDestroyed + isSessionActive ガードで制御する。
    this.webSocketService.connectionStatus$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        pairwise(),
        filter(
          ([prev, curr]) =>
            !prev.connected && curr.connected && this.isSessionActive,
        ),
      )
      .subscribe(() => {
        console.log(
          '[Reconnect] WebSocket reconnected during active session, restarting Cloud STT turn',
        )
        if (this.recognitionMode === 'cloudSTT') {
          this.isCloudSttTurnActive = false
          this.lastTurnStartTime = null
          this.startCloudSttTurn().catch((err) => {
            console.error(
              'Failed to restart Cloud STT turn after reconnection:',
              err,
            )
          })
        }
      })

    // ===== Phase 1: New Service Subscriptions =====

    // Subscribe to dialogue state changes
    this.dialogueStateMachine
      .getCurrentState()
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((state: DlgState) => {
        console.log(`[DialogueStateMachine] State changed to: ${state}`)
        // Map DialogueStateMachineService state to component's internal state is handled separately

        // Update UI flags based on state
        switch (state) {
          case 'IDLE':
            this.isAIPlaying = false
            this.isAIProcessing = false
            break
          case 'LISTENING':
            this.isAIPlaying = false
            this.isAIProcessing = false
            break
          case 'USER_SPEAKING':
            this.isAIPlaying = false
            this.isAIProcessing = false
            break
          case 'PROCESSING':
            this.isAIPlaying = false
            this.isAIProcessing = true
            break
          case 'AI_SPEAKING':
            this.isAIPlaying = true
            this.isAIProcessing = false
            break
        }
      })

    // NOTE: AudioQueue購読は startSession() 内の setupAudioQueueSubscriptions() でセットアップする。
    // ngOnInit() からは呼ばない（二重購読防止のため）。

    // ===== Original WebSocket Subscriptions (to be migrated to facade) =====

    // WebSocketからの文字起こし結果（従来のMediaRecorder方式用）
    this.webSocketService.transcription$
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((result) => {
        if (!this.useSpeechRecognition && this.view === 'DIALOG') {
          if (result && result.sessionId === this.sessionId) {
            this.handleTranscriptionResult(result)
          }
        }
      })

    // Speech Recognition結果（新しい方式）
    this.speechRecognitionService.transcript$
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((result) => {
        if (this.useSpeechRecognition && this.view === 'DIALOG') {
          this.handleSpeechRecognitionResult(result).catch((error) => {
            console.error('Failed to handle speech recognition result:', error)
          })
        }
      })

    // Speech Recognition状態監視
    this.speechRecognitionService.status$
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((status) => {
        if (this.useSpeechRecognition && this.view === 'DIALOG') {
          if (status.isListening && !status.isProcessing) {
            this.toListening()
          } else if (status.isProcessing) {
            this.toProcessing()
          }
          if (status.error) {
            console.error('Speech recognition error:', status.error)
            this.showAlert(status.error)
          }
        }
      })

    // 評価データの購読と会話ログ記録（Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.7, 3.6）
    // ファサードのsetupSubscriptions()内でassessmentDataSubject.next()がaiResponseSubject.next()より先に
    // 同期emitされるため、このハンドラはAI発話ログ追加より先に実行される
    this.webSocketFacade
      .getAssessmentData$()
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe(async (data) => {
        // 1. 評価ログエントリをconversationsに追加（AI発話エントリの直前）
        this.conversations.push({
          type: 'assessment',
          content: data.reason ?? '',
          timestamp: new Date(),
          assessment: data.assessment,
          reason: data.reason,
          speaker: data.speaker,
          reasonDetail: data.reasonDetail,
          score: data.score,
        })

        // 2. 最新の評価データを表示用プロパティに保持
        this.currentAssessment = data

        // 3. SE再生（アドオン提供時のみ、assessmentが存在する場合のみ）
        if (this.soundEffect != null && data.assessment != null) {
          await this.soundEffect.play(data.assessment)
        }
      })

    // Use RoleplayWebSocketFacadeService instead of direct subscription
    // The facade automatically enqueues to AudioQueueService (no duplicate enqueue needed)
    this.webSocketFacade
      .getAiResponse$()
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((response) => {
        console.log('aiResponse$ (via facade) triggered:', {
          hasResponse: !!response,
          responseSessionId: response?.sessionId,
          currentSessionId: this.sessionId,
          match: response?.sessionId === this.sessionId,
        })

        if (response && response.sessionId === this.sessionId) {
          console.log('🤖 AI応答:', response.response)

          this.sessionService.setAiFinishedPending(response.isFinished || false)

          this.isWaitingForAiResponse = false

          // 2者通話： 話者更新
          this.currentSpeaker = response.speaker ?? null

          // 会話履歴に追加
          this.conversations.push({
            type: 'ai',
            content: response.response,
            timestamp: new Date(response.timestamp),
            audioData: response.audioData,
            metadata: response.metadata,
          })

          // 弾幕にAI発話を追加
          if (this.showWaveformDebug) {
            this.audioAnalyzerService.addTranscript(response.response, 'ai')
            this.debugVisualizationService.addTranscript(
              response.response,
              'ai',
            )
          }

          // ===== Enqueue is handled automatically by RoleplayWebSocketFacadeService =====
          // No need to manually enqueue here - the facade already does it via tap()

          // デバッグ注入でaudioDataが空の場合、enqueueがスキップされるため
          // AI_SPEAKINGに遷移するとgetQueueCompleted()が発火せず状態が固定される。
          // この場合はLISTENINGに遷移して操作可能な状態を維持する。（Req 3.6, 5.4）
          const isDebugInjectionWithoutAudio =
            response.metadata?.['source'] === 'debug-injection' &&
            !response.audioData
          if (isDebugInjectionWithoutAudio) {
            this.toListening()
          } else {
            this.dialogueStateMachine.transition(DlgState.AI_SPEAKING)
          }
        }
      })

    this.webSocketService.sessionStatus$
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe(async (status) => {
        if (status.status === 'started' && status.selectedScenario) {
          console.log('Selected scenario received:', status.selectedScenario)
          this.selectedScenario = status.selectedScenario

          if (this.personaData) {
            this.sessionTitle = this.generateSessionTitle(
              this.personaData,
              status.selectedScenario,
            )
            console.log(
              'Session title updated with selected scenario:',
              this.sessionTitle,
            )
          }
        }

        if (
          status.sessionId === this.sessionId &&
          (status.status === 'ai_error' ||
            status.status === 'transcription_error')
        ) {
          console.error('Session error:', status.message)

          this.isWaitingForAiResponse = false
          this.isAIProcessing = false
          this.stopCurrentAudio()

          // ===== Phase 1: Use AudioQueueService =====
          this.audioQueueService.clear()

          // エラー音声を再生
          if (status.audioData) {
            try {
              this.toAiSpeaking()
              await this.playSingleAudio(status.audioData)
            } catch (e) {
              console.error('Failed to play error audio:', e)
            }
          }

          // LISTENING状態に戻して音声認識を再開
          this.toListening()
          if (this.useSpeechRecognition) {
            setTimeout(
              () => this.speechRecognitionService.onProcessingComplete(),
              300,
            )
          }
        }
      })

    this.webSocketService.aiProcessing$
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((isProcessing) => {
        if (this.view !== 'DIALOG') return

        this.isAIProcessing = isProcessing
        if (isProcessing) {
          this.toProcessing()
        }
      })

    this.webSocketService.sessionEnded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data.sessionId === this.sessionId) {
          console.log('Session ended event received:', data)

          // アセスメントIDを保存
          if (data.assessmentId) {
            this.assessmentId = data.assessmentId
            console.log('Assessment ID received:', this.assessmentId)
          }
        }
      })

    // 従来のMediaRecorder方式用の購読（useSpeechRecognition=falseの場合のみ）
    this.audioRecordingService.audioChunk$
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe({
        next: (chunk) => {
          if (
            !this.useSpeechRecognition &&
            chunk.isComplete &&
            chunk.audioData &&
            this.personaData
          ) {
            this.webSocketService.sendAudioData(
              this.sessionId,
              chunk.audioData,
              true,
              chunk.sequence,
              this.personaData,
            )
          }
        },
        error: (error) => {
          console.error('Audio chunk subscription error:', error)
        },
      })

    this.audioRecordingService.audioLevel$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.sessionEnded$),
        auditTime(50),
        observeOn(animationFrameScheduler),
      )
      .subscribe((audioInfo) => {
        if (!this.useSpeechRecognition) {
          this.currentAudioInfo = audioInfo
          this.updateStateBasedOnAudioInfo(audioInfo)
        }
      })

    this.audioRecordingService.processing$
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((isProc) => {
        if (this.view !== 'DIALOG') return

        if (!this.useSpeechRecognition) {
          this.isAIProcessing = isProc
          if (isProc) {
            this.toProcessing()
          } else if (!this.isAIPlaying) {
            this.toListening()
          }
        }
      })

    // Use RoleplayWebSocketFacadeService instead of direct subscription
    // The facade automatically enqueues filler audio to AudioQueueService
    this.webSocketFacade
      .getBridgeAudio$()
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((bridgeAudio: BridgeAudioEvent) => {
        console.log(`🎵 Bridge audio received (via facade):`, bridgeAudio)

        if (bridgeAudio.sessionId === this.sessionId) {
          console.log(
            `🎵 Bridge audio (filler) auto-enqueued by facade: ${bridgeAudio.fillerId}`,
          )
          // ===== Enqueue is handled automatically by RoleplayWebSocketFacadeService =====
          // No need to manually enqueue here - the facade already does it via tap()
        }
      })

    // Cloud STT: interim transcript
    // Use RoleplayWebSocketFacadeService to prevent duplicate subscriptions
    this.webSocketFacade
      .getInterimTranscript$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: InterimTranscriptEvent) => {
        if (
          this.recognitionMode === 'cloudSTT' &&
          event.sessionId === this.sessionId
        ) {
          this.currentInterimText = event.accumulatedText
          // 弾幕に暫定トランスクリプトを追加
          if (this.showWaveformDebug && event.accumulatedText.trim()) {
            this.debugVisualizationService.addTranscript(
              event.accumulatedText,
              'interim',
            )
          }
          // 発話中状態に遷移
          if (this.state !== 'USER_SPEAKING') {
            this.toUserTalking()
          }
        }
      })

    // Cloud STT: user turn started (coordinator auto-start対応)
    this.webSocketService.userTurnStarted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (
          this.recognitionMode === 'cloudSTT' &&
          event.sessionId === this.sessionId
        ) {
          this.isCloudSttTurnActive = true
          console.log('🎤 ユーザーターン開始')
        }
      })

    // Cloud STT: user turn complete
    this.webSocketService.userTurnComplete$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: UserTurnCompleteEvent) => {
        console.log('[DEBUG] userTurnComplete$ triggered:', {
          recognitionMode: this.recognitionMode,
          eventSessionId: event?.sessionId,
          currentSessionId: this.sessionId,
          match: event?.sessionId === this.sessionId,
          text: event?.fullText?.substring(0, 30),
        })

        if (
          this.recognitionMode === 'cloudSTT' &&
          event.sessionId === this.sessionId &&
          this.isSessionActive
        ) {
          console.log('🏁 Cloud STT user turn complete:', event.fullText)
          this.isCloudSttTurnActive = false

          // 会話履歴に追加 + 処理中状態に遷移（テキストがある場合のみ）
          if (event.fullText.trim()) {
            this.conversations.push({
              type: 'user',
              content: event.fullText,
              timestamp: new Date(),
            })

            // 弾幕にユーザー発話を追加
            if (this.showWaveformDebug) {
              this.audioAnalyzerService.addTranscript(event.fullText, 'user')
              this.debugVisualizationService.addTranscript(
                event.fullText,
                'user',
              )
            }

            // 処理中状態に遷移（テキストが空の場合はスキップ）
            this.toProcessing()
          }
          this.currentInterimText = ''
        }
      })

    // Cloud STT: AI turn complete
    this.webSocketService.aiTurnComplete$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (
          this.recognitionMode === 'cloudSTT' &&
          event.sessionId === this.sessionId
        ) {
          console.log('Cloud STT AI turn complete', {
            skipped: event.skipped,
            reason: event.reason,
          })

          // スキップされた場合（空のトランスクリプト等）は即座に次のターンを開始
          if (event.skipped) {
            console.log(
              `⚠️ Turn skipped (reason: ${event.reason}), CloudSttTurnCoordinator will restart automatically`,
            )

            // NOTE: CloudSttTurnCoordinator handles skipped turns automatically
            // Do not call startCloudSttTurn() here to avoid duplicate turn starts
            return
          }

          // 即座に開始せず、フラグを立てる
          this.aiTurnCompleteReceived = true

          // NOTE: CloudSttTurnCoordinator handles next turn start automatically
          // No need to call startNextUserTurn() here
        }
      })

    // Cloud STT: STT error
    this.webSocketService.sttError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: SttErrorEvent) => {
        if (event.sessionId === this.sessionId) {
          this.isCloudSttTurnActive = false
          this.currentInterimText = ''

          if (!this.isSessionActive) {
            // セッション終了後の STT エラーは想定内のためアラートも Sentry 送信もしない（stream エラーは roleplay-ws 側で capture 済み）
            console.info('Post-session STT error (ignored):', event.error)
            return
          }

          console.error('Cloud STT error:', event.error)
          if (event.canRetry && this.personaData) {
            // リトライ可能ならターン再開
            setTimeout(() => this.startCloudSttTurn(), 1000)
          } else {
            this.showAlert(`音声認識エラー: ${event.error}`)
          }
        }
      })

    // Cloud STT: Audio streaming chunks
    this.audioStreamingService.audioChunk$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((chunk) => {
        if (
          this.recognitionMode === 'cloudSTT' &&
          this.isCloudSttTurnActive &&
          this.isSessionActive
        ) {
          this.webSocketService.sendAudioStream(
            this.sessionId,
            chunk.data,
            chunk.sequence,
          )
        }
      })

    // Cloud STT: Audio level for UI
    this.audioStreamingService.audioLevel$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        auditTime(50),
        observeOn(animationFrameScheduler),
      )
      .subscribe((level) => {
        if (this.recognitionMode === 'cloudSTT' && this.view === 'DIALOG') {
          this.currentAudioInfo = {
            currentLevel: level.level,
            isDetectingVoice: level.isSpeaking,
            speechDuration: 0,
            confidence: 0,
          }
        }
      })

    this.sessionService
      .getAiFinishedPending()
      .pipe(auditTime(1500))
      .subscribe((val) => {
        val && this.handleAiFinishedSession()
      })
  }

  onStopAiSpeaking(): void {
    console.log('⏸ User requested AI speaking stop')
    this.stopCurrentAudio()

    // ===== Phase 1: Use AudioQueueService =====
    this.audioQueueService.clear()

    this.isAIPlaying = false
    this.isWaitingForAiResponse = false

    // Transition to IDLE state
    this.dialogueStateMachine.transition(DlgState.IDLE)

    // 会話受付に戻る
    if (this.isSessionActive) {
      this.toListening()

      // 音声認識を再開
      if (this.recognitionMode === 'cloudSTT') {
        // Cloud STTモード: 新しいターンを開始
        setTimeout(() => this.startCloudSttTurn(), 300)
      } else if (this.recognitionMode === 'webSpeech') {
        this.speechRecognitionService.setWaitingForResponse(false)
        setTimeout(() => {
          this.speechRecognitionService.onProcessingComplete()
        }, 300)
      } else {
        setTimeout(() => {
          this.audioRecordingService.onAudioProcessingComplete()
        }, 300)
      }
    }
  }

  private stopCurrentAudio(): void {
    const el = this.aiEl
    if (!el) return

    try {
      el.pause()
      el.currentTime = 0
      el.onended = null
      el.onerror = null
      console.log('🛑 Current audio stopped')
    } catch (error) {
      console.error('Failed to stop current audio:', error)
    }
  }

  private updateStateBasedOnAudioInfo(audioInfo: AudioLevelInfo): void {
    if (this.isAIPlaying) {
      return
    }

    if (this.isAIProcessing) {
      return
    }

    if (performance.now() < this.startSilenceUntil) {
      if (this.isSessionActive) this.toListening()
      return
    }

    if (audioInfo.isDetectingVoice) {
      this.toUserTalking()
    } else if (this.isSessionActive) {
      this.toListening()
    }
  }

  // Speech Recognition結果のハンドリング（新方式）
  private async handleSpeechRecognitionResult(
    result: SpeechRecognitionResult,
  ): Promise<void> {
    if (!this.personaData) {
      console.error('Persona data not available')
      return
    }

    // AI応答待ちフラグを立てる
    if (this.useSpeechRecognition) {
      this.speechRecognitionService.setWaitingForResponse(true)
    }

    console.log('🗣️ Speech recognition result:', {
      transcript: result.transcript,
      confidence: result.confidence,
      hasAudio: !!result.audioData,
    })

    // 会話履歴に追加
    this.conversations.push({
      type: 'user',
      content: result.transcript,
      timestamp: result.timestamp,
      confidence: Math.round(result.confidence * 100),
      audioData: result.audioData,
    })

    // 弾幕にユーザー発話を追加
    if (this.showWaveformDebug) {
      this.audioAnalyzerService.addTranscript(result.transcript, 'user')
      this.debugVisualizationService.addTranscript(result.transcript, 'user')
    }

    this.isWaitingForAiResponse = true

    // WebSocketでテキストをサーバーに送信
    this.sendTextToServer(
      result.transcript,
      result.confidence,
      result.audioData,
    )
  }

  // テキストメッセージをサーバーに送信
  private async sendTextToServer(
    text: string,
    confidence: number,
    audioData?: Blob,
  ): Promise<void> {
    if (!this.personaData) {
      console.error('Persona data not available')
      return
    }

    console.log('📤 Sending text to server:', {
      text: text.substring(0, 50),
      hasAudio: !!audioData,
    })

    let audioDataBase64: string | undefined = undefined
    if (audioData) {
      audioDataBase64 =
        await this.audioFormatAdapterService.blobToBase64(audioData)
    }

    this.webSocketService.sendTextMessage(
      this.sessionId,
      text,
      this.personaData,
      confidence,
      audioDataBase64,
    )
  }

  // 従来の文字起こし結果ハンドリング（MediaRecorder方式）
  private handleTranscriptionResult(result: TranscriptionResult): void {
    this.transcriptions.push(result)

    this.conversations.push({
      type: 'user',
      content: result.transcription,
      timestamp: new Date(result.timestamp),
      confidence: this.currentAudioInfo.confidence,
    })

    // 弾幕にユーザー発話を追加
    if (this.showWaveformDebug) {
      this.audioAnalyzerService.addTranscript(result.transcription, 'user')
      this.debugVisualizationService.addTranscript(result.transcription, 'user')
    }

    console.log(`User message added: ${result.transcription}`)
  }

  onToggleCaptions(e?: Event) {
    const input = e?.target as HTMLInputElement | undefined
    const next =
      input && typeof input.checked === 'boolean'
        ? input.checked
        : !this.captionsEnabled
    this.captionsEnabled = next
  }

  async onMicChange(id: string) {
    if (this.view !== 'PREPARE') return
    if (this.isSwitchingMic) return

    this.isSwitchingMic = true
    try {
      if (this.micTestRunning) {
        await this.stopTestMic()
      }

      // Save device selection
      this.audioDevices.setMic(id)

      // Update UI label
      const list = await firstValueFrom(this.mics$)
      this.micTitle =
        list?.find((x) => x.deviceId === id)?.label || 'システム既定'

      // Reinitialize strategy with new device (Task 15.2)
      if (this.currentStrategy) {
        const deviceIdOrNull = id === '' ? null : id
        await this.initializeAudioStrategy(deviceIdOrNull)
      }
    } finally {
      this.isSwitchingMic = false
    }
  }

  async onSpeakerChange(id: string) {
    if (this.view !== 'PREPARE') return
    this.audioDevices.setSpeaker(id)
    if (this.sinkCapable && this.aiAudioRef?.nativeElement) {
      await this.audioDevices.ensureOutputPermission(id ?? '')
    }
  }

  async testMic() {
    if (this.view !== 'PREPARE' || this.micTestRunning) return
    this.micTestRunning = true

    try {
      await this.audioRecordingService.startLevelMonitorOnly()

      const tick = () => {
        const raw = this.audioRecordingService.getCurrentAudioLevel()
        this.rms = Math.max(0, Math.min(1, raw / 20))
        if (this.micTestRunning) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    } catch {
      this.micTestRunning = false
      this.rms = 0
      this.audioRecordingService.stopLevelMonitorOnly()
      await this.showAlert(
        'マイクにアクセスできません。権限やデバイス設定を確認してください。',
      )
    }
  }

  async stopTestMic() {
    this.micTestRunning = false
    this.rms = 0
    this.audioRecordingService.stopLevelMonitorOnly()
  }

  private get aiEl(): HTMLAudioElement {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    return this.aiAudioRef?.nativeElement!
  }

  async testSpeaker() {
    if (this.view !== 'PREPARE') return
    const el = this.aiEl
    if (!el) return

    const AUDIO_BASE64 = testvoice.audioContent
    try {
      el.pause()
      el.currentTime = 0

      if (!el.src) {
        const blob = this.audioDevices.base64ToBlob(AUDIO_BASE64, 'audio/wav')
        const url = URL.createObjectURL(blob)
        el.src = url
      }

      if (this.sinkCapable) {
        const id = await firstValueFrom(this.selectedSpeakerId$)
        await this.audioDevices.ensureOutputPermission(id ?? '')
        await this.audioDevices.applySinkId(el, id ?? '')
      }

      await el.play()
    } catch (e) {
      console.warn('testSpeaker failed:', e)
      await this.showAlert(
        'スピーカーのテストに失敗しました。ブラウザ設定や権限をご確認ください。',
      )
    }
  }

  adjustVADSensitivity(delta: number): void {
    this.vadSettings.silenceThreshold = Math.max(
      1,
      Math.min(50, this.vadSettings.silenceThreshold + delta),
    )
    this.audioRecordingService.updateVADSettings(this.vadSettings)
    console.log(
      `VAD sensitivity adjusted to: ${this.vadSettings.silenceThreshold}`,
    )
  }

  adjustSilenceDuration(delta: number): void {
    this.vadSettings.silenceDuration = Math.max(
      500,
      Math.min(5000, this.vadSettings.silenceDuration + delta),
    )
    this.audioRecordingService.updateVADSettings(this.vadSettings)
    console.log(
      `Silence duration adjusted to: ${this.vadSettings.silenceDuration}ms`,
    )
  }

  adjustMinSpeechDuration(delta: number): void {
    this.vadSettings.minSpeechDuration = Math.max(
      100,
      Math.min(2000, this.vadSettings.minSpeechDuration + delta),
    )
    this.audioRecordingService.updateVADSettings(this.vadSettings)
    console.log(
      `Min speech duration adjusted to: ${this.vadSettings.minSpeechDuration}ms`,
    )
  }

  // toggle系メソッドはaudio-analyzerに移譲
  async toggleEchoCancellation(): Promise<void> {
    await this.audioAnalyzerService.toggleEchoCancellation()
  }

  async toggleNoiseSuppression(): Promise<void> {
    await this.audioAnalyzerService.toggleNoiseSuppression()
  }

  async toggleAutoGainControl(): Promise<void> {
    await this.audioAnalyzerService.toggleAutoGainControl()
  }

  /**
   * アバターをタップしたときの処理（7タップでデバッグモード切り替え）
   *
   * DebugVisualizationServiceに委譲してデバッグ可視化を管理
   *
   * Requirements: 11.9, 22.3
   */
  onAvatarTap(): void {
    // 本番環境では無効
    if (environment.production) {
      return
    }

    // タイマーをリセット
    if (this.avatarTapTimer) {
      clearTimeout(this.avatarTapTimer)
    }

    this.avatarTapCount++
    console.log(`Avatar tap count: ${this.avatarTapCount}/7`)

    if (this.avatarTapCount >= 7) {
      this.showWaveformDebug = !this.showWaveformDebug
      console.log(
        `🎵 Waveform debug mode: ${this.showWaveformDebug ? 'ON' : 'OFF'}`,
      )
      this.avatarTapCount = 0

      if (this.showWaveformDebug) {
        // デバッグモードONの場合 - DebugVisualizationServiceに委譲
        // AudioRecordingServiceから音声データソースを設定
        this.debugVisualizationService.setAudioDataSources({
          getFrequencyData: () => this.audioRecordingService.getFrequencyData(),
          getCurrentAudioInfo: () =>
            this.audioRecordingService.getCurrentAudioInfo(),
          getVADSettings: () => this.audioRecordingService.getVADSettings(),
        })

        if (this.view === 'DIALOG') {
          // Angularの変更検知後にcanvasが設定されるのを待つ
          setTimeout(() => {
            this.debugVisualizationService.start()
          }, 0)
        }
      } else {
        // デバッグモードOFFの場合、描画を停止
        this.debugVisualizationService.stop()
      }
    } else {
      // 2秒以内に次のタップがない場合、カウントをリセット
      this.avatarTapTimer = setTimeout(() => {
        this.avatarTapCount = 0
      }, 2000)
    }
  }

  /**
   * Cloud STT: 次のユーザーターンを開始（音声再生完了後に呼ばれる）
   */
  private startNextUserTurn(): void {
    // 連続開始防止
    const now = Date.now()
    if (this.lastTurnStartTime != null && now - this.lastTurnStartTime < 1000) {
      console.warn(
        `Too fast, waiting... (elapsed: ${now - this.lastTurnStartTime}ms)`,
      )
      return
    }

    // フラグをリセット
    this.aiTurnCompleteReceived = false

    // ユーザーターンを開始
    this.startCloudSttTurn()
  }

  /**
   * Cloud STT: ユーザーターンを開始
   *
   * CloudSttTurnCoordinatorServiceに委譲してターンライフサイクル全体を管理
   *
   * Requirements: 8.1, 22.1
   */
  private async startCloudSttTurn(): Promise<void> {
    if (!this.personaData || !this.isSessionActive) {
      console.log('Cannot start Cloud STT turn: session not active')
      return
    }

    if (this.isCloudSttTurnActive) {
      console.log('Cloud STT turn already active')
      return
    }

    // 前回のターン開始から1秒以内の場合はブロック（無限ループ防止）
    const now = Date.now()
    if (this.lastTurnStartTime != null && now - this.lastTurnStartTime < 1000) {
      console.log(
        `Blocking rapid turn start (elapsed: ${now - this.lastTurnStartTime}ms)`,
      )
      return
    }

    console.log('🎤 Starting Cloud STT user turn via CloudSttTurnCoordinator')
    this.isCloudSttTurnActive = true
    this.lastTurnStartTime = now
    this.currentInterimText = ''

    // デバイスIDを取得
    const micId = await firstValueFrom(this.selectedMicId$)

    // CloudSttTurnCoordinatorServiceに委譲
    await this.turnCoordinator.startTurn(
      this.sessionId,
      this.personaData,
      micId ?? 'default',
    )
  }

  /**
   * Cloud STT: ユーザーターンを手動終了
   *
   * CloudSttTurnCoordinatorServiceに委譲してターンを停止
   *
   * Requirements: 22.1
   */
  private async endCloudSttTurn(): Promise<void> {
    // isCloudSttTurnActive が false（AI応答フェーズ等）でも coordinator は
    // currentSessionId を保持しており、セッション終了後に届く ai-turn-complete で
    // ターンを自動再開してしまう（SBL-149: 孤児STTストリーム → 408）。
    // フラグに関係なく常に stopTurn() を呼び、自動再開を解除する
    console.log('Ending Cloud STT user turn via CloudSttTurnCoordinator')
    await this.turnCoordinator.stopTurn()
    this.isCloudSttTurnActive = false
  }

  async startSession(): Promise<void> {
    // touchend + click の二重発火防止（isConnecting はバリデーション後に立つため不十分）
    if (this.isStartingSession || this.isConnecting) {
      return
    }
    this.isStartingSession = true
    try {
      await this.startSessionInner()
    } finally {
      this.isStartingSession = false
    }
  }

  private async startSessionInner(): Promise<void> {
    if (this.micTestRunning) {
      await this.stopTestMic()
    }

    // Safari autoplay policy 対策: user gesture 内で各 audio 要素の autoplay を解除する。
    // play() を await しないことで user gesture を AudioContext にも温存する。
    // 無音 WAV は 0 サンプルなので即座に ended し、pause() の競合リスクはない。
    // iPad Safari では <audio> 要素ごとに unlock が必要なため SE 用要素も対象にする。
    const silentWav =
      'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='
    const aiEl = this.aiEl
    const unlockTargets: HTMLAudioElement[] = []
    if (aiEl) unlockTargets.push(aiEl)
    if (this.soundEffectAudioRef?.nativeElement) {
      unlockTargets.push(this.soundEffectAudioRef.nativeElement)
    }
    for (const el of unlockTargets) {
      el.muted = true
      el.src = silentWav
      el.play()
        .catch(() => {})
        .finally(() => {
          el.muted = false
        })
    }

    // Reset session duration
    this.sessionDurationSeconds = 0

    // Validation
    if (!this.selectedPersonaId) {
      await this.showAlert('ペルソナを選択してください。')
      return
    }
    if (!this.personaData || !this.selectedPersona) {
      console.error('Persona data not loaded')
      await this.showAlert('ペルソナデータの読み込みに失敗しました。')
      return
    }
    if (!this.personaData.skillMapId) {
      await this.showAlert(
        'ペルソナスキルマップが設定されていません。ペルソナ設定を確認してください。',
      )
      return
    }
    if (!this.personaData.phaseIds || this.personaData.phaseIds.length === 0) {
      await this.showAlert(
        'ペルソナフェーズが設定されていません。ペルソナ設定を確認してください。',
      )
      return
    }

    this.sessionTitle = this.generateSessionTitle(this.personaData, null)
    this.isConnecting = true

    try {
      await this.webSocketService.connect()

      const micId = await firstValueFrom(this.selectedMicId$)
      const speakerId = await firstValueFrom(this.selectedSpeakerId$)
      const deviceInfo = this.audioDevices.getDeviceInfo(micId, speakerId)
      this.webSocketService.sendDeviceInfo(this.sessionId, deviceInfo)

      this.audioDevices.lockSelections()
      this.view = 'DIALOG'
      this.toListening()

      // Start session tracking through service
      await this.sessionService.startSession(this.sessionId, this.personaData)

      // Start WebSocket facade subscriptions
      await this.webSocketFacade.startSession(this.sessionId, this.personaData)

      // Setup AudioQueue subscriptions for this session
      this.setupAudioQueueSubscriptions()

      // Start session duration timer (updates every second)
      interval(1000)
        .pipe(takeUntil(this.sessionEnded$))
        .subscribe(() => {
          this.sessionDurationSeconds = Math.floor(
            this.sessionService.getSessionDurationMs() / 1000,
          )
        })

      this.webSocketService.startRoleplaySession(
        this.sessionId,
        this.personaData,
        this.personaData.skillMapId,
        this.personaData.phaseIds,
      )
      this.isSessionActive = true

      // 選択したモードに応じて音声認識を開始
      if (this.recognitionMode === 'cloudSTT') {
        // Cloud STT Streaming を使用
        console.log('🎤 Starting Cloud STT Streaming mode')
        await this.audioStreamingService.initialize(micId)
        await this.audioStreamingService.startStreaming()
        // 最初のターンを開始
        this.startCloudSttTurn()
      } else if (this.recognitionMode === 'webSpeech') {
        // Web Speech API を使用
        console.log('🎤 Starting Speech Recognition mode')
        await this.audioRecordingService.startLevelMonitorOnly()
        this.speechRecognitionService.start(micId)
      } else {
        // 従来のMedia Recorder方式を使用
        console.log('🎤 Starting MediaRecorder mode')
        this.audioRecordingService.updateVADSettings(this.vadSettings)
        await this.audioRecordingService.startContinuousRecording()
      }

      this.startSilenceUntil = performance.now() + 500
      this.toListening()

      // 波形デバッグモードがONの場合のみ描画開始
      if (this.showWaveformDebug) {
        this.audioAnalyzerService.start()
      }

      const modeText =
        this.recognitionMode === 'cloudSTT'
          ? 'Cloud STT Streaming'
          : this.recognitionMode === 'webSpeech'
            ? 'Web Speech API'
            : 'MediaRecorder + Server STT'
      console.log(`Enhanced session started with ${modeText} mode`)
    } catch (error) {
      this.view = 'PREPARE'
      console.error('Failed to start session:', error)
      await this.showAlert('セッションの開始に失敗しました。')
    } finally {
      this.isConnecting = false
    }
  }

  async onEndClick() {
    const sessionDurationMs = this.sessionService.getSessionDurationMs()
    const sessionDurationSeconds = Math.floor(sessionDurationMs / 1000)

    if (!this.sessionService.isMinimumDurationMet()) {
      const message =
        `会話時間が短いため、アセスメントを作成できません。\n\n` +
        `現在の会話時間: ${sessionDurationSeconds}秒\n` +
        `必要な時間: 90秒以上\n\n` +
        `対話を終了しますか？`
      if (await this.showConfirm(message)) {
        this.endSessionWithoutAssessment()
      }
    } else if (this.conversations.length === 0) {
      const message =
        `会話データが存在しないため、アセスメントを作成できません。\n\n` +
        `対話を終了しますか？`
      if (await this.showConfirm(message)) {
        this.endSessionWithoutAssessment()
      }
    } else {
      if (await this.showConfirm('対話を終了しますか？')) {
        this.endSession()
      }
    }
  }

  private endSessionWithoutAssessment(): void {
    console.log(
      'endSessionWithoutAssessment called - skipping assessment creation',
    )

    // フェードアニメーション完了後に呼ばれるため、フラグをリセット
    this.isTransitioning = false

    try {
      // Update final session duration before stopping the timer
      this.sessionDurationSeconds = Math.floor(
        this.sessionService.getSessionDurationMs() / 1000,
      )

      // セッション終了を通知（全ての subscription が自動停止）
      this.sessionEnded$.next()

      this.stopCurrentAudio()

      // ===== Phase 1: Use AudioQueueService =====
      this.audioQueueService.clear()
      console.log('🔄 Audio queue cleared via AudioQueueService')

      this.isAIPlaying = false

      // 使用しているモードに応じて停止処理
      if (this.recognitionMode === 'cloudSTT') {
        this.endCloudSttTurn()
        this.audioStreamingService.stopStreaming()
        this.isCloudSttTurnActive = false
        this.currentInterimText = ''
      } else if (this.recognitionMode === 'webSpeech') {
        this.speechRecognitionService.stop()
        this.audioRecordingService.stopLevelMonitorOnly()
      } else {
        if (this.audioRecordingService) {
          this.audioRecordingService.stopContinuousRecording()
        }
      }

      if (this.webSocketService && this.webSocketService.isConnected()) {
        this.webSocketService.endRoleplaySession(this.sessionId, false)
      }

      this.audioAnalyzerService.stop()
      this.isSessionActive = false
      this.isAIProcessing = false
      this.isAIPlaying = false
      this.state = 'IDLE'

      this.audioDevices.unlockSelections()

      this.view = 'HISTORY'

      console.log('Session ended without assessment creation')
    } catch (error) {
      console.error('Error during session termination:', error)

      this.isSessionActive = false
      this.isAIProcessing = false
      this.isAIPlaying = false
      this.isCloudSttTurnActive = false
      this.view = 'PREPARE'
      this.state = 'IDLE'
    }
  }

  async endSession(): Promise<void> {
    console.log('endSession method called')

    // フェードアニメーション完了後に呼ばれるため、フラグをリセット
    this.isTransitioning = false

    // セッション終了フラグを最初に設定（以降のWebSocketイベントを無視するため）
    this.isSessionActive = false

    try {
      // Update final session duration before stopping the timer
      this.sessionDurationSeconds = Math.floor(
        this.sessionService.getSessionDurationMs() / 1000,
      )

      // セッション終了を通知（全ての subscription が自動停止）
      this.sessionEnded$.next()

      // 次のセッション用に新しいSubjectを作成（AudioQueue購読を再開可能にする）
      this.sessionEnded$ = new Subject<void>()

      this.stopCurrentAudio()

      // ===== Phase 1: Use AudioQueueService =====
      this.audioQueueService.clear()

      this.isAIPlaying = false

      // 使用しているモードに応じて停止処理
      if (this.recognitionMode === 'cloudSTT') {
        this.endCloudSttTurn()
        this.audioStreamingService.stopStreaming()
        this.isCloudSttTurnActive = false
        this.currentInterimText = ''
      } else if (this.recognitionMode === 'webSpeech') {
        this.speechRecognitionService.stop()
        this.audioRecordingService.stopLevelMonitorOnly()
      } else {
        if (this.audioRecordingService) {
          this.audioRecordingService.stopContinuousRecording()
        }
      }

      if (this.webSocketService && this.webSocketService.isConnected()) {
        const shouldCreateAssessment =
          this.sessionService.isMinimumDurationMet()

        this.webSocketService.endRoleplaySession(
          this.sessionId,
          shouldCreateAssessment,
        )
      }

      // End session tracking
      await this.sessionService.endSession()

      // End WebSocket facade subscriptions
      await this.webSocketFacade.endSession()

      this.audioAnalyzerService.stop()
      this.isAIProcessing = false
      this.isAIPlaying = false
      this.state = 'IDLE'

      this.audioDevices.unlockSelections()

      this.view = 'HISTORY'

      console.log('Session ended successfully')
    } catch (error) {
      console.error('Error during session termination:', error)

      this.isAIProcessing = false
      this.isAIPlaying = false
      this.view = 'PREPARE'
      this.state = 'IDLE'
    }
  }

  /**
   * AudioQueue購読をセットアップ
   *
   * セッション開始時に呼び出され、音声再生に必要な購読を確立します。
   * セッション終了時に自動的にunsubscribeされます。
   */
  private setupAudioQueueSubscriptions(): void {
    console.log('[AudioQueue] Setting up audio queue subscriptions')

    // Subscribe to current playing item from audio queue
    this.audioQueueService
      .getCurrentItem()
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe(async (item) => {
        if (item != null) {
          console.log(`[AudioQueue] Now playing item:`, {
            type: item.type,
            source: item.source,
            dataLength: item.audioData?.length,
          })

          // Transition to AI_SPEAKING state only when actual AI response plays (not filler)
          // Filler audio should play in PROCESSING state to show thinking.png
          if (item.type === 'response') {
            this.toAiSpeaking()
          }

          // Play the audio using the existing playSingleAudio method
          try {
            await this.playSingleAudio(item.audioData)
            console.log(`[AudioQueue] Audio playback completed successfully`)

            // Notify AudioQueueService that playback is complete
            this.audioQueueService.completeCurrentPlayback()
          } catch (error) {
            console.error(`[AudioQueue] Audio playback failed:`, error)

            // Even on error, complete the current item to prevent queue blocking
            this.audioQueueService.completeCurrentPlayback()
          }
        } else {
          console.log(
            `[AudioQueue] No item currently playing (cleared or completed)`,
          )
        }
      })

    // Subscribe to audio queue state changes
    // Subscribe to queue completion events
    this.audioQueueService
      .getQueueCompleted()
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe(() => {
        console.log('[AudioQueue] Queue completed')

        // Transition back to LISTENING state when audio queue is empty
        this.toListening()

        // Handle Cloud STT turn logic: CloudSttTurnCoordinator handles automatic turn restart
        if (
          this.recognitionMode === 'cloudSTT' &&
          this.isSessionActive &&
          this.aiTurnCompleteReceived &&
          this.personaData
        ) {
          console.log(
            '✅ Queue complete + aiTurnComplete received → CloudSttTurnCoordinator will start next turn automatically',
          )
          // NOTE: CloudSttTurnCoordinator handles the next turn automatically via prepareNextTurn()
          // Do not call startNextUserTurn() here to avoid duplicate turn starts
        }
      })

    // Subscribe to queue length changes
    this.audioQueueService
      .getQueueLength()
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.sessionEnded$))
      .subscribe((length) => {
        console.log(`[AudioQueue] Queue length: ${length}`)
      })
  }

  clearConversations(): void {
    this.conversations = []
    this.transcriptions = []
  }

  get canCreateAssessment(): boolean {
    // セッション終了後はsessionServiceの状態がリセットされるため、
    // コンポーネント側で保存したsessionDurationSecondsを使用
    return this.sessionDurationSeconds >= 90
  }

  getCurrentAudioLevel(): number {
    if (this.useSpeechRecognition) {
      return this.audioRecordingService.getCurrentAudioLevel()
    }
    return this.currentAudioInfo.currentLevel
  }

  get currentStatus(): string {
    if (!this.isSessionActive) return 'セッション停止中'

    switch (this.state) {
      case 'AI_SPEAKING':
        return 'AI応答中...'
      case 'PROCESSING':
        return 'AI処理中...'
      case 'USER_SPEAKING':
        if (this.useSpeechRecognition) {
          return '話し中...'
        }
        // eslint-disable-next-line no-case-declarations
        const duration =
          Math.round((this.currentAudioInfo.speechDuration / 1000) * 10) / 10
        return `話し中... (${duration}s, 品質: ${this.currentAudioInfo.confidence}%)`
      case 'LISTENING':
        if (this.useSpeechRecognition) {
          return '聞いています...'
        }
        return `聞いています... (音声レベル: ${this.currentAudioInfo.currentLevel.toFixed(1)})`
      default:
        return '待機中'
    }
  }

  get personaDisplayName(): string {
    if (!this.personaData) return 'ペルソナ未設定'
    return this.personaData.name
  }

  get userSubtitle(): string {
    if (
      (this.state === 'USER_SPEAKING' || this.state === 'LISTENING') &&
      this.currentInterimText.trim()
    ) {
      return this.currentInterimText
    }
    if (this.state === 'PROCESSING') {
      const lastUserMsg = [...this.conversations]
        .reverse()
        .find((m) => m.type === 'user')
      return lastUserMsg?.content ?? ''
    }
    return ''
  }

  get aiSubtitle(): string {
    if (this.state === 'AI_SPEAKING') {
      const lastAiMsg = [...this.conversations]
        .reverse()
        .find((m) => m.type === 'ai')
      return lastAiMsg?.content ?? ''
    }
    return ''
  }

  get isGroupSession(): boolean {
    return this.personaData?.occupation.match(/^\[-2者通話-\].*/) ? true : false
  }

  get micIcon(): string {
    switch (this.state) {
      case 'AI_SPEAKING':
        return '🎧'
      case 'USER_SPEAKING':
        return '🎙️'
      case 'PROCESSING':
        return ''
      case 'LISTENING':
        return '🎤'
      default:
        return '🔇'
    }
  }

  get audioLevelPercentage(): number {
    if (this.recognitionMode === 'cloudSTT') {
      return Math.min(100, (this.currentAudioInfo.currentLevel / 50) * 100)
    }
    if (this.recognitionMode === 'webSpeech') {
      return this.state === 'LISTENING' ? 40 : 0
    }
    return Math.min(100, (this.currentAudioInfo.currentLevel / 50) * 100)
  }

  get recognitionModeLabel(): string {
    switch (this.recognitionMode) {
      case 'cloudSTT':
        return '☁️ Cloud STT Streaming'
      case 'webSpeech':
        return '🚀 高速モード (Web Speech API)'
      default:
        return '🎯 高精度モード (Server STT)'
    }
  }

  /**
   * デバッグJSON注入: テキストエリアのJSONをパースしてAIレスポンスパイプラインに注入する
   *
   * バリデーション → sessionId/timestamp自動補完 → Facade.injectAiResponse()呼び出し
   * 不正JSONの場合はdebugJsonErrorにエラーメッセージを表示し、注入を実行しない
   *
   * Requirements: 2.3, 2.4（実装はTask 2.2で完成）
   */
  onInjectJson(): void {
    this.debugJsonError = null

    if (!this.debugJsonInput || this.debugJsonInput.trim() === '') {
      this.debugJsonError = 'JSONを入力してください'
      return
    }

    let parsed: AIResponse
    try {
      parsed = JSON.parse(this.debugJsonInput)
    } catch (e) {
      this.debugJsonError = `JSONパースエラー: ${(e as Error).message}`
      return
    }

    if (!parsed.sessionId) {
      parsed.sessionId = this.sessionId
    }
    if (!parsed.timestamp) {
      parsed.timestamp = new Date().toISOString()
    }

    this.webSocketFacade.injectAiResponse(parsed)
  }

  // アセスメント連携ボタン
  async onAssessment() {
    if (!this.assessmentId) {
      await this.showAlert(
        'アセスメントIDが取得できていません。少々お待ちください。',
      )
      return
    }

    if (!this.userId) {
      await this.showAlert('ユーザーIDが取得できていません。')
      return
    }

    this.skipDeactivateConfirm = true
    this.router.navigate(['/home'], {
      queryParams: {
        'user-id': this.userId,
        'assessment-id': this.assessmentId,
      },
    })
  }

  get assessmentResultLink(): string[] {
    if (!this.assessmentId || !this.userId) {
      return ['/home']
    }
    return ['/home']
  }

  get assessmentResultQueryParams(): { [key: string]: string } | null {
    if (!this.assessmentId || !this.userId) {
      return null
    }
    return {
      'user-id': this.userId,
      'assessment-id': this.assessmentId,
    }
  }

  private getGenderImageDirectory(): string {
    if (!this.personaData || !this.personaData.gender) {
      return 'female' // デフォルトは女性
    }

    return this.personaData.gender === '男性' ? 'male' : 'female'
  }

  get avatarImage(): string {
    const genderDir = this.getGenderImageDirectory()

    switch (this.state) {
      case 'USER_SPEAKING':
      case 'LISTENING':
        // 応答待ち
        return `assets/image/${genderDir}/listening.png`

      case 'PROCESSING':
        // 考え中
        return `assets/image/${genderDir}/thinking.png`

      case 'AI_SPEAKING':
        // 2者通話モードで子が話している場合は親はlistening
        if (this.isGroupSession && this.currentSpeaker === '子') {
          return `assets/image/${genderDir}/listening.png`
        }

        // 発言中（1種類の画像のみ）
        return `assets/image/${genderDir}/speaking-halfopen.png`

      default:
        return `assets/image/${genderDir}/listening.png`
    }
  }

  // 2 者通話用の暫定的実装
  // 将来的には avatarImage と統合する
  get childAvatarImage(): string {
    const childGenderDir = 'child-male'

    switch (this.state) {
      case 'USER_SPEAKING':
      case 'LISTENING':
        return `assets/image/${childGenderDir}/listening.png`

      case 'PROCESSING':
        return `assets/image/${childGenderDir}/thinking.png`

      case 'AI_SPEAKING':
        // 2者通話モードで母が話している場合は子はlistening
        if (this.isGroupSession && this.currentSpeaker === '母') {
          return `assets/image/${childGenderDir}/listening.png`
        }

        return `assets/image/${childGenderDir}/speaking-halfopen.png`

      default:
        return `assets/image/${childGenderDir}/listening.png`
    }
  }

  private generateSessionTitle(
    personaData: PersonaData,
    selectedScenario: { id: string; name: string } | null,
  ): string {
    const now = new Date()

    const year = now.getFullYear().toString().slice(-2)
    const month = (now.getMonth() + 1).toString().padStart(2, '0')
    const day = now.getDate().toString().padStart(2, '0')
    const hour = now.getHours().toString().padStart(2, '0')
    const minute = now.getMinutes().toString().padStart(2, '0')
    const dateTimeStr = `${year}/${month}/${day}/${hour}:${minute}`

    // シナリオ情報（サーバーから届くまでは「シナリオ選択中」）
    const scenarioInfo = selectedScenario
      ? selectedScenario.name
      : 'シナリオ選択中'

    return `${personaData.name}_${dateTimeStr}_${scenarioInfo}`
  }

  private handleAiFinishedSession(): void {
    const shouldCreateAssessment =
      this.sessionService.getSessionDurationMs() >= 90000
    const finishFunc = shouldCreateAssessment
      ? this.endSession.bind(this)
      : this.endSessionWithoutAssessment.bind(this)
    this.isTransitioning = true
    setTimeout(finishFunc, 500)
  }

  private playSingleAudio(audioData: string): Promise<void> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      console.log(
        `🎵 playSingleAudio started (data length: ${audioData?.length})`,
      )

      const el = this.aiEl
      if (!el) {
        console.error(`❌ Audio element not available`)
        reject(new Error('Audio element not available'))
        return
      }

      try {
        // スピーカー設定を適用
        if (this.sinkCapable) {
          const id = await firstValueFrom(this.selectedSpeakerId$)
          await this.audioDevices.ensureOutputPermission(id ?? '')
          await this.audioDevices.applySinkId(el, id ?? '')
        }

        el.pause()
        el.onended = null
        el.onerror = null
        el.src = `data:audio/mpeg;base64,${audioData}`
        el.currentTime = 0
        el.volume = 0.8

        el.onended = () => {
          console.log('✅ Audio segment ended')
          resolve()
        }

        el.onerror = (error) => {
          console.error('❌ Audio playback error:', error)
          reject(error)
        }

        console.log(`▶️ Starting audio playback...`)
        await el.play()
        console.log('✅ play() succeeded')
      } catch (error) {
        console.error('❌ Failed to setup/play audio:', error)
        reject(error)
      }
    })
  }

  // カスタムアラートダイアログ関連関数
  showAlert(message: string): Promise<void> {
    return new Promise((resolve) => {
      this.alertConfig = {
        type: 'alert',
        message,
        confirmText: 'OK',
        cancelText: '',
        resolve: () => resolve(),
      }
    })
  }

  showConfirm(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.alertConfig = {
        type: 'confirm',
        message,
        confirmText: 'はい',
        cancelText: 'いいえ',
        resolve,
      }
    })
  }

  confirmAlert() {
    if (this.alertConfig?.resolve) {
      this.alertConfig.resolve(true)
    }
    this.alertConfig = null
  }

  hideAlert() {
    if (this.alertConfig?.resolve) {
      this.alertConfig.resolve(false)
    }
    this.alertConfig = null
  }

  // ===== Phase 2: Strategy Pattern Helper Methods =====

  /**
   * Initialize audio capture strategy for the selected recognition mode
   *
   * Creates a new strategy instance using the factory and initializes it with the specified device ID.
   * If a previous strategy exists, it will be destroyed first.
   *
   * Requirements:
   * - Task 15.1: Use AudioCaptureStrategyFactory for mode switching
   * - 要件 1.6: 音声認識モード切り替えをストラテジーパターンに抽出
   * - 要件 6.1: 認識モード選択時にストラテジーインスタンス化
   *
   * @param deviceId - The microphone device ID to use, or null for default device
   */
  private async initializeAudioStrategy(
    deviceId: string | null,
  ): Promise<void> {
    try {
      // Cleanup previous strategy if exists
      if (this.currentStrategy) {
        await this.strategyFactory.destroyStrategy(this.currentStrategy)
        this.currentStrategy = null
      }

      // Create new strategy based on current recognition mode
      this.currentStrategy = this.strategyFactory.createStrategy(
        this.recognitionMode as RecognitionMode,
      )

      // Initialize strategy with device ID
      await this.currentStrategy.initialize(deviceId)

      console.log(
        `✅ Audio capture strategy initialized: ${this.recognitionMode}`,
      )
    } catch (error) {
      console.error('Failed to initialize audio strategy:', error)
      throw error
    }
  }

  /**
   * Stop audio capture and cleanup the current strategy
   *
   * Requirements:
   * - Task 15.1: Cleanup strategy on session end
   */
  private async cleanupAudioStrategy(): Promise<void> {
    if (this.currentStrategy) {
      try {
        await this.strategyFactory.destroyStrategy(this.currentStrategy)
        this.currentStrategy = null
        console.log(
          `✅ Audio capture strategy cleaned up: ${this.recognitionMode}`,
        )
      } catch (error) {
        console.error('Error cleaning up audio strategy:', error)
      }
    }
  }

  /**
   * Get the currently selected microphone ID from AudioDevicesService
   *
   * Requirements:
   * - Task 15.2: Device management integration
   * - Requirement 11.7: Device selection logic preservation
   *
   * @returns Promise<string> - The selected microphone device ID (or empty string for default)
   */
  private async getSelectedMicId(): Promise<string> {
    const micId = await firstValueFrom(this.selectedMicId$)
    return micId ?? ''
  }

  /**
   * Initialize audio strategy with device validation
   *
   * Validates that the saved device ID exists in the available devices list before initialization.
   * Falls back to default device if saved ID is invalid.
   *
   * Requirements:
   * - Task 15.2: Device management integration
   * - Requirement 7.2: Device ID validation
   * - Requirement 11.7: Device selection logic preservation
   */
  private async initializeAudioStrategyWithValidation(): Promise<void> {
    try {
      // Refresh device list to ensure up-to-date availability
      await this.audioDevices.refreshDevices()

      // Get currently saved device ID
      const savedDeviceId = await this.getSelectedMicId()

      // Validate device ID against available devices
      const availableDevices = await firstValueFrom(this.mics$)
      const isDeviceValid =
        savedDeviceId === '' ||
        availableDevices.some((device) => device.deviceId === savedDeviceId)

      if (!isDeviceValid) {
        // Invalid device ID - clear it and use default
        console.warn(
          `Saved device ID "${savedDeviceId}" not found in available devices. Using default device.`,
        )
        this.audioDevices.setMic('')
        await this.initializeAudioStrategy(null)
      } else {
        // Valid device ID - use it for initialization
        const deviceIdOrNull = savedDeviceId === '' ? null : savedDeviceId
        await this.initializeAudioStrategy(deviceIdOrNull)
      }
    } catch (error) {
      console.error(
        'Failed to initialize audio strategy with validation:',
        error,
      )
      throw error
    }
  }
}
